import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { LogOut, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startLogin = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Admin = () => {
  const location = useLocation();
  const [authed, setAuthed] = useState(location.state?.user ? true : null); // null=checking
  const [user, setUser] = useState(location.state?.user || null);
  const [bookings, setBookings] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [notAuthorized, setNotAuthorized] = useState(false);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return; // AuthCallback handles it
    const check = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data); setAuthed(true);
      } catch {
        setAuthed(false);
      }
    };
    if (authed === null) check();
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    const load = async () => {
      try {
        const [b, a] = await Promise.all([
          axios.get(`${API}/admin/bookings`, { withCredentials: true }),
          axios.get(`${API}/admin/analytics`, { withCredentials: true }),
        ]);
        setBookings(b.data); setAnalytics(a.data); setNotAuthorized(false);
      } catch (e) {
        if (e?.response?.status === 403) setNotAuthorized(true);
      }
    };
    load();
  }, [authed]);

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    setAuthed(false); setUser(null);
  };

  if (authed === null) {
    return <div className="min-h-screen bg-navy grid place-items-center text-white/60 font-sans" data-testid="admin-loading">Loading…</div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-navy grid place-items-center px-6">
        <div className="w-full max-w-sm bg-white rounded-3xl p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-champagne/30 text-navy grid place-items-center mx-auto mb-5">
            <ShieldCheck size={26} />
          </div>
          <h1 className="font-serif text-3xl text-navy">Revive Admin</h1>
          <p className="font-sans text-sm text-charcoal/60 mt-2">Sign in with your authorized clinic Google account to view consultation requests.</p>
          <button onClick={startLogin}
            className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-full border border-navy/15 bg-white py-3.5 font-medium text-navy hover:bg-offwhite transition-colors"
            data-testid="google-signin">
            <img alt="" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" />
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  if (notAuthorized) {
    return (
      <div className="min-h-screen bg-navy grid place-items-center px-6">
        <div className="w-full max-w-sm bg-white rounded-3xl p-8 text-center" data-testid="admin-not-authorized">
          <div className="w-14 h-14 rounded-full bg-red-100 text-red-600 grid place-items-center mx-auto mb-5">
            <ShieldCheck size={26} />
          </div>
          <h1 className="font-serif text-2xl text-navy">Access not authorized</h1>
          <p className="font-sans text-sm text-charcoal/60 mt-2">
            {user?.email ? `${user.email} is not on the Revive admin allowlist.` : "This account is not authorized for Revive admin access."}
          </p>
          <button onClick={logout} className="mt-6 w-full rounded-full bg-navy text-white py-3 font-medium" data-testid="admin-not-authorized-signout">
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-offwhite p-6 sm:p-10">
      <Seo title="Admin" robots="noindex, nofollow" path="/admin" />
      <div className="max-w-[1200px] mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-serif text-4xl text-navy">Consultation Requests</h1>
            {user && <p className="font-sans text-sm text-navy/50 mt-1">Signed in as {user.email}</p>}
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy" data-testid="admin-logout">
            <LogOut size={16} /> Sign out
          </button>
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          {Object.entries(analytics).map(([k, v]) => (
            <div key={k} className="rounded-2xl bg-white border border-navy/5 px-5 py-4">
              <p className="font-serif text-3xl text-navy">{v}</p>
              <p className="font-sans text-xs uppercase tracking-wide text-navy/50 mt-1">{k.replace(/_/g, " ")}</p>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-dashed border-navy/20 bg-white p-4 mt-6 text-xs text-navy/60 font-sans">
          Note: Publish only real, authorized patient testimonials with appropriate permission. Do not fabricate patient outcomes or testimonials.
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-navy/5 bg-white">
          <table className="w-full text-sm font-sans" data-testid="admin-bookings">
            <thead className="bg-navy text-white text-left">
              <tr>
                {["Date", "Name", "Type", "Email", "Phone", "Country", "Interest", "Pref. Date", "SMS", "Notified"].map((h) => (
                  <th key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bookings.map((r) => (
                <tr key={r.id} className="border-t border-navy/5 hover:bg-offwhite">
                  <td className="px-4 py-3 whitespace-nowrap text-charcoal/60">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-medium text-navy">{r.first_name} {r.last_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.consultation_type}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.email}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.phone}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.country}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.treatment_interest}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.preferred_date || "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {r.sms_consent
                      ? <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Opted in</span>
                      : <span className="text-navy/30">No</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {r.email_notified === false ? (
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">Failed</span>
                    ) : r.email_notified ? (
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Sent</span>
                    ) : (
                      <span className="text-navy/30">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {bookings.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-navy/40">No consultation requests yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Admin;
